import { Component, Vue } from 'vue-property-decorator';
import ParticipantService from '@/services/participant.service';
import MedicalData from '@/components/MedicalData/MedicalData';
import RenounceButton from '@/components/RenounceButton/RenounceButton';
import { MedicalDataDTO } from '@/models/MedicalDataDTO';
import ApiError from '@/exceptions/api.error';
import { McsisRole } from '@/models/constants/McsisRole';
import ConfigService from '@/services/config.service';
import { ConfigItemDTO } from '@/models/ConfigItemDTO';
import { store } from '@/store/store';
import InformationBlock from '@/components/InformationBlock/InformationBlock';

@Component({
  components: {
    MedicalData,
    RenounceButton,
    InformationBlock,
  },
})
export default class MedicalContent extends Vue {
  public partId!: number;
  public medicalData: MedicalDataDTO = {} as MedicalDataDTO;
  public updating?: boolean = false;
  public doctorUpdating?: boolean = false;
  public submitted: boolean = false;
  public errorMessage: string = '';
  public error: boolean = false;
  public avsError: boolean = false;
  public roleParticipant: string = McsisRole[McsisRole.Participant];
  public roleDoctor: string = McsisRole[McsisRole.Doctor];
  public isDoctor: boolean = false;
  public canUpdateDoctor: boolean = false;
  private valid: boolean = false;

  protected mounted() {
    this.partId = Number(this.$route.params.partId);

    ConfigService.fetchConfigs('InclusionMedicalData').then((items: ConfigItemDTO[]) => {
        const canUpdateDoctorConfig = items.find((i) => i.name === 'CanUpdateDoctor');
        if (canUpdateDoctorConfig && canUpdateDoctorConfig.value === 'False') {
          this.canUpdateDoctor = false;
        } else {
          this.canUpdateDoctor = true;
        }
    });

    ParticipantService.fetchMedicalDataContent(this.partId)
      .then((response: any) => {
        if (response.partId !== undefined) {
          this.medicalData = response as MedicalDataDTO;
        }
      })
      .catch((error: ApiError) => {
        this.error = true;
      });

    // By default, the user will edit its medical data, so on this
    // updating must be true in order for MedicalData to display
    // accordingly
    this.updating = true;
    // By default, a doctor will not edit its information, unless he clicks
    this.$isInRole(McsisRole.Doctor).then((isInRole: boolean) => {
      this.isDoctor = isInRole;
      this.doctorUpdating = false; // fields are not in updating mode for doctors
    });
  }

  /**
   * Handles the validity of the AVS field
   * @param v True is the field is valid.
   */
  protected handleFormValidation(v: boolean): void {
    this.valid = v;
  }

  protected pushToNextStep() {
    if (this.hasCustomConsent()) {
      this.$router.push({
        name: 'consent',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    } else {
      this.$router.push({
        name: 'recap',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    }
  }

  /**
   * Handles the validity of both insurance number field and
   * AVS number field.
   * At least one of the field must be filled, and in case of
   * AVS, it should also be valid.
   */
  protected validate(): boolean {

    // An AVS number exists => check if it's valid
    // It's not important to check insuranceNumber here
    if (!!this.medicalData.avsNumber) {
      this.avsError = !this.valid;
      return this.valid;
    }

    // No AVS number exists
    if (!this.medicalData.avsNumber) {
      // No insurance number exists
      // Normally HLTM5 will automatically handle this case through required attribute
      if (!this.medicalData.insuranceNumber) {
        return false;
      }

      // Insurance number exists
      if (!!this.medicalData.insuranceNumber) {
        return true;
      }
    }

    return false;
  }

  /**
   * Actions to do before submitting the form:
   *  - get the data from child MedicalData
   *  - validate and submit directly if the checkbox noDoctor is checked
   */
  protected onSubmit() {
    // We call the emitUpdateMedicalData method from the child component (MedicalData).
    // For some reason, TS is throwing an error on the next line, which
    // is not an error from a Vue point of view. The solution is working
    // so we exceptionally ignore this error.
    // @ts-ignore
    this.$refs.medicalDataChild.emitUpdateMedicalData();

    if (this.validate()) {
      this.submit();
    }
  }

  /**
   * Submit the form and go to next step.
   */
  protected submit() {
    if (this.validate()) {
      ParticipantService.saveMedicalData(this.medicalData)
        .then((response: any) => {
          this.submitted = true;
          this.pushToNextStep();
        })
        .catch((error: ApiError) => {
          this.error = true;
        });
    }
  }

  protected onUpdateMedicalData(data: MedicalDataDTO) {
    this.medicalData = data;
  }

  private hasCustomConsent(): boolean {
    return store.state.hasCustomConsent;
  }
}

